import React from 'react';
import {useSignalBuilderStore} from './SignalBuilder.state';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  GridItem,
  Spinner,
} from '@chakra-ui/react';
import {LabelInput} from './helpers';
import Select from 'react-select';
import {useFieldDefinition} from '../../../hooks/api/metadata';
import {AccountFieldOrCustomField} from '../../../shared/signalDefinition';
import {isAccountField} from '../../../shared/fields/keyplay';
import {
  CrmFieldDefinition,
  isCrmFieldDefinition,
} from '../../../shared/fields/enrichment';
import {useConnectedCrmIntegration, useCrmFields} from '../../../hooks/api/crm';
import {CrmIntegrationType} from '../../../shared/integrations/crm';

const ErrorMessage = ({message}: {message: string}) => (
  <GridItem colSpan={2}>
    <Alert status="warning">
      <AlertIcon />
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  </GridItem>
);

export const PicklistSignalBuilder = ({
  field,
  isDisabled = false,
}: {
  field: AccountFieldOrCustomField;
  isDisabled?: boolean;
}) => {
  const fieldDefinition = useFieldDefinition(
    isAccountField(field) ? undefined! : field
  );
  const crmIntegration = useConnectedCrmIntegration();

  if (isAccountField(field)) {
    return <ErrorMessage message="Cannot create signals for account fields." />;
  }

  if (!fieldDefinition) {
    return <ErrorMessage message="Field definition not found." />;
  }

  if (!isCrmFieldDefinition(fieldDefinition)) {
    return <ErrorMessage message="Field must be a CRM picklist field." />;
  }

  if (!crmIntegration) {
    return <ErrorMessage message="No CRM integration connected." />;
  }

  return (
    <_PicklistSignalBuilder
      field={field}
      fieldDefinition={fieldDefinition}
      integrationName={crmIntegration.type}
      isDisabled={isDisabled}
    />
  );
};

const _PicklistSignalBuilder = ({
  field,
  fieldDefinition,
  integrationName,
  isDisabled,
}: {
  field: AccountFieldOrCustomField;
  fieldDefinition: CrmFieldDefinition;
  integrationName: CrmIntegrationType;
  isDisabled: boolean;
}) => {
  const {evalFn, setEvalFn, setLabel} = useSignalBuilderStore();
  const {data: crmFields, isPending} = useCrmFields(integrationName);

  if (evalFn && evalFn.type !== 'equal' && evalFn.type !== 'in') {
    return <ErrorMessage message="Unsupported signal evaluation type." />;
  }

  const crmField = crmFields?.find(
    (f) => f.name === fieldDefinition.config.fieldName
  );
  const picklistOptions = (crmField?.options ?? [])
    .map((option) => ({
      value: option.value,
      label: option.label,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const evalType =
    fieldDefinition.dataType === 'multiPicklist' ? 'in' : 'equal';

  const update = (value: string, label: string) => {
    setEvalFn({
      type: evalType,
      field,
      value,
    });
    setLabel(label);
  };

  if (isPending) {
    return (
      <GridItem colSpan={2} display="flex" justifyContent="center" py="4">
        <Spinner size="sm" />
      </GridItem>
    );
  }

  return (
    <>
      <GridItem as={Flex} alignItems="center" flexDirection="row" gap="4">
        <Box w="100%">
          <Select
            isDisabled={isDisabled}
            isMulti={false}
            options={picklistOptions}
            value={picklistOptions.find(
              (option) => option.value === evalFn?.value
            )}
            onChange={(newValue) => {
              if (!newValue) {
                return;
              }

              update(newValue.value, newValue.label);
            }}
            placeholder="Select a value"
          />
        </Box>
      </GridItem>
      <GridItem alignSelf="center">
        <LabelInput />
      </GridItem>
    </>
  );
};
